import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { ConsentEntryPayload } from '../../domains/CommunicationCenter/utils/buildConsentPayload';
import {
  ChannelsByPurposeMap,
  CommunicationChannel,
  ConsentPurpose,
  TabData,
  UserConsentRecord,
} from '../../domains/CommunicationCenter/types/types';
import { useAuth } from './AuthContext';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../shared/hooks/use-my-vw-context';
import { useUser } from './UserContext';

interface CommunicationCenterContextType {
  communicationChannels: CommunicationChannel[] | null;
  tabs: TabData[];
  isLoading: boolean;
  error: boolean | null;
  /**
   * True once Q3 (getUserConsentData) has resolved at least once — success
   * or failure. Q3 failures don't block isLoading/error (see fetchConsent),
   * but consumers that need to safely SEED editable state off of tabs (e.g.
   * the Save/Cancel form hook) should wait for this too, so they don't seed
   * from an "all unchecked" snapshot moments before the real data lands.
   */
  isConsentDataSettled: boolean;
  userConsentData: UserConsentRecord[] | null;
  /** True once the user has at least one existing consent record. */
  hasExistingConsent: boolean;
  /** Already-uppercased userId, ready to use in mutation payloads. */
  userId: string | undefined;
  refetchConsentData: () => Promise<void>;
  retryFetch: () => Promise<void>;
  /**
   * Updates userConsentData directly from the `consents` array that was just
   * submitted to createConsent, without waiting on the backend. Needed
   * because:
   * 1) getUserConsentData lags a few seconds behind a save — refetching
   *    immediately after save risks showing stale (pre-save) data.
   * 2) This context is mounted at the app level and outlives the
   *    CommunicationCenter page component, so this is the only place an
   *    optimistic update can live and still survive the user navigating
   *    away and back.
   * Takes the exact payload.consents that were sent, so the cached state is
   * guaranteed to match what was actually submitted — no separate mapping
   * logic to keep in sync with buildConsentPayload.
   */
  applyOptimisticConsentUpdate: (consents: ConsentEntryPayload[]) => void;
}

const CommunicationCenterContext = createContext<CommunicationCenterContextType | undefined>(undefined);

export const CommunicationCenterProvider = ({ children }: { children: React.ReactNode }) => {
  const [communicationChannels, setCommunicationChannels] = useState<CommunicationChannel[] | null>(null);
  const [consentPurposes, setConsentPurposes] = useState<ConsentPurpose[] | null>(null);
  const [channelsByPurpose, setChannelsByPurpose] = useState<ChannelsByPurposeMap | null>(null);
  const [userConsentData, setUserConsentData] = useState<UserConsentRecord[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean | null>(null);
  // True once Q3 (getUserConsentData) has settled at least once — success or
  // failure, both are terminal for this flag. Q3 doesn't block the page's
  // own loading spinner (a failure there is non-blocking, see the Q3 effect
  // below), but it DOES need to gate anything that edits userConsentData —
  // otherwise a user could start toggling checkboxes off an "all unchecked"
  // snapshot before the real consent data has arrived, and have their
  // in-progress edits silently overwritten when it lands.
  const [isConsentDataSettled, setIsConsentDataSettled] = useState<boolean>(false);

  const staticDataFetched = useRef<boolean>(false);
  const consentDataFetched = useRef<boolean>(false);

  const { myvwFaRolloutCommunicationCenter } = useFlags();

  const { authService } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();
  const { isAuthenticated } = useAuth();
  const { user } = useUser();

  const localeCode = `${localeService.language}-${localeService.countryCode}`;
  const marketId = 'VW-USA';
  const userId = user?.id?.toUpperCase() || user?.vwId?.toUpperCase();


  // ── Query 1: market-wide channel list (drives column headers) ──────────────
  const fetchCommunicationChannels = async (): Promise<CommunicationChannel[]> => {
    const { getCommunicationChannel } = await authService.getCommunicationChannels(marketId, localeCode);
    return getCommunicationChannel;
  };

  // ── Query 2: purposes grouped into tabs ───────────────────────────────────
  const fetchConsentPurposes = async (): Promise<ConsentPurpose[]> => {
    const { getConsentPurposes } = await authService.getConsentPurposesByMarketId(marketId, localeCode);
    return getConsentPurposes;
  };

  // ── Query 3: user opt-in state ─────────────────────────────────────────────
  const fetchUserConsentData = useCallback(async (): Promise<UserConsentRecord[] | null> => {
    if (!userId) return null;

    const { getUserConsentData: data } = await authService.getUserConsentData(userId, localeCode);

    return data?.consents ?? null;
  }, [authService, userId, localeCode]);

  // ── Query 4: supported channels per purpose (needs Q2 results first) ───────
  const fetchChannelsByPurpose = async (purposes: ConsentPurpose[]): Promise<ChannelsByPurposeMap> => {
    const responses = await Promise.all(
      purposes.map((purpose) =>
        authService.getCommunicationChannelsByPurposeId(purpose.purposeId, localeCode),
      ),
    );

    return purposes.reduce<ChannelsByPurposeMap>((acc, purpose, index) => {
      acc[purpose.purposeId] = responses[index].getCommunicationChannelsByPurposeId;
      return acc;
    }, {});
  };

  // ── Fetch static data (Q1/Q2/Q4) ────────────────────────────────────────────
  // Market-level data — doesn't depend on which user is logged in.
  const fetchStaticData = useCallback(async () => {
    try {
      staticDataFetched.current = true;
      setIsLoading(true);
      setError(null);

      const [channels, purposes] = await Promise.all([
        fetchCommunicationChannels(),
        fetchConsentPurposes(),
      ]);

      const channelMap = purposes ? await fetchChannelsByPurpose(purposes) : null;

      setCommunicationChannels(channels);
      setConsentPurposes(purposes);
      setChannelsByPurpose(channelMap);
    } catch (err) {
      console.error('Error fetching communication center static data:', err);
      staticDataFetched.current = false;
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [authService, marketId, localeCode]);

  useEffect(() => {
    if (!myvwFaRolloutCommunicationCenter || !isAuthenticated || staticDataFetched.current) return;

    fetchStaticData();
  }, [myvwFaRolloutCommunicationCenter, isAuthenticated, fetchStaticData]);

  // ── Fetch user consent data (Q3) once userId is available
  useEffect(() => {
    const fetchConsent = async () => {
      if (!myvwFaRolloutCommunicationCenter || !isAuthenticated || !userId || consentDataFetched.current) return;

      try {
        consentDataFetched.current = true;
        const consentData = await fetchUserConsentData();
        setUserConsentData(consentData);
      } catch (err) {
        console.error('Error fetching user consent data:', err);
        consentDataFetched.current = false;
        setUserConsentData(null);
      } finally {
        setIsConsentDataSettled(true);
      }
    };

    fetchConsent();
  }, [myvwFaRolloutCommunicationCenter, isAuthenticated, userId, fetchUserConsentData]);

  useEffect(() => {
    if (!isAuthenticated) {
      staticDataFetched.current = false;
      consentDataFetched.current = false;
      setCommunicationChannels(null);
      setConsentPurposes(null);
      setChannelsByPurpose(null);
      setUserConsentData(null);
      setIsLoading(false);
      setError(null);
      setIsConsentDataSettled(false);
    }
  }, [isAuthenticated]);

  const refetchConsentData = useCallback(async () => {
    try {
      setIsLoading(true);
      const consentData = await fetchUserConsentData();
      consentDataFetched.current = true;
      setUserConsentData(consentData);
    } catch (error) {
      console.error('Error fetching user consent data:', error);
      consentDataFetched.current = false;
      setUserConsentData(null);
    } finally {
      setIsLoading(false);
      setIsConsentDataSettled(true);
    }
  }, [fetchUserConsentData]);

  // ── Optimistic update after a successful save ──────────────────────────────
  // Converts the just-submitted createConsent payload.consents back into the
  // UserConsentRecord[] shape tabs derives from, so the UI reflects the save
  // immediately without waiting on getUserConsentData to catch up.
  const applyOptimisticConsentUpdate = useCallback((consents: ConsentEntryPayload[]) => {
    setUserConsentData((previous) => {
      // Start from the full existing records — keyed by purposeId so we can
      // update in place. This is a merge, not a replace: purposes not present
      // in the incoming consents (e.g. saved on a previous call) are kept as-is.
      const merged = new Map((previous ?? []).map((record) => [record.purpose.id, record]));

      consents.forEach((consent) => {
        const previousRecord = merged.get(consent.purpose.purposeId);

        merged.set(consent.purpose.purposeId, {
          recordId: previousRecord?.recordId ?? consent.purpose.purposeId,
          purpose: {
            id: consent.purpose.purposeId,
            name: previousRecord?.purpose.name ?? '',
            isActive: previousRecord?.purpose.isActive ?? true,
          },
          channels: consent.channels
            .filter((channel) => channel.optIn === 'Y')
            .map((channel) => ({
              recordId: `${consent.purpose.purposeId}-${channel.channelId}`,
              optIn: true,
              channelValue: channel.channelValue,
              channel: {
                id: channel.channelId,
                definition:
                  previousRecord?.channels.find((c) => c.channel?.id === channel.channelId)?.channel
                    ?.definition ?? '',
              },
            })),
        });
      });

      return Array.from(merged.values());
    });
    consentDataFetched.current = true;
  }, []);

  // ── Retry after an error — re-runs whichever queries haven't succeeded yet ──
  // The error banner can only appear when Q1/Q2/Q4 failed (Q3 failures are
  // non-blocking), so retry re-runs the static data fetch. If Q3 also hasn't
  // resolved yet (e.g. it failed silently in the background), re-run it too.
  const retryFetch = useCallback(async () => {
    staticDataFetched.current = false;
    await fetchStaticData();

    if (!consentDataFetched.current) {
      await refetchConsentData();
    }
  }, [fetchStaticData, refetchConsentData]);

  const tabs = useMemo<TabData[]>(() => {
    if (!communicationChannels || !consentPurposes || !channelsByPurpose) return [];

    const groupedByCategory = consentPurposes.reduce<Record<string, ConsentPurpose[]>>(
      (acc, purpose) => {
        const tabKey = purpose.category.name;
        if (!acc[tabKey]) acc[tabKey] = [];
        acc[tabKey].push(purpose);
        return acc;
      },
      {},
    );

    return Object.entries(groupedByCategory).map(([categoryName, purposes]) => ({
      key: categoryName,
      label: categoryName,
      purposes: purposes.map((purpose) => {
        const supportedChannels = channelsByPurpose[purpose.purposeId] ?? [];

        const supportedChannelIds = new Set(supportedChannels.map((c) => c.id));

        const consentRecord = userConsentData?.find(
          (record) => record.purpose.id === purpose.purposeId,
        );

        return {
          purposeId: purpose.purposeId,
          name: purpose.name,
          description: purpose.definition,
          channels: communicationChannels.map((marketChannel) => {
            const isSupported = supportedChannelIds.has(marketChannel.id);

            const isOptedIn =
              isSupported &&
              !!consentRecord?.channels?.some(
                (ch) => ch.channel?.id === marketChannel.id && ch.optIn,
              );

            return { channelName: marketChannel.name, isSupported, isOptedIn };
          }),
        };
      }),
    }));
  }, [communicationChannels, consentPurposes, channelsByPurpose, userConsentData]);

  const hasExistingConsent = !!userConsentData && userConsentData.length > 0;

  return (
    <CommunicationCenterContext.Provider
      value={{
        communicationChannels,
        tabs,
        isLoading,
        error,
        isConsentDataSettled,
        userConsentData,
        hasExistingConsent,
        userId,
        refetchConsentData,
        retryFetch,
        applyOptimisticConsentUpdate,
      }}>
      {children}
    </CommunicationCenterContext.Provider>
  );
};

export const useCommunicationCenter = () => {
  const context = useContext(CommunicationCenterContext);

  if (!context) throw new Error('useCommunicationCenterContext must be used within CommunicationCenterProvider');

  return context;
};
